/**
 * "Connect a backup folder" — the only genuinely automatic backup the web
 * platform offers.
 *
 * Where the File System Access API exists (Chromium on desktop and Android), a
 * directory handle can be persisted in IndexedDB and re-used across sessions,
 * so the vault file can be rewritten after every save with no further
 * interaction. Safari has no such thing and never silently writes to disk;
 * there the nudge chip and the share sheet are the whole story.
 *
 * Permission is not guaranteed to survive: the browser may demand a fresh
 * gesture, and asking for one without user intent throws. Every failure here
 * downgrades to nudge mode rather than surfacing an error — a backup folder
 * that has gone quiet is a reason to show the chip again, not a reason to
 * interrupt a game.
 */

import { buildVaultBackup, markBackedUp } from './vault-backup';
import { setBackupDestination } from './durability';

interface FsPermissionDescriptor {
  mode?: 'read' | 'readwrite';
}
interface FsWritable {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
}
interface FsFileHandle {
  createWritable(): Promise<FsWritable>;
}
interface FsDirectoryHandle {
  kind: 'directory';
  name: string;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FsFileHandle>;
  queryPermission?(descriptor?: FsPermissionDescriptor): Promise<PermissionState>;
  requestPermission?(descriptor?: FsPermissionDescriptor): Promise<PermissionState>;
}

const DB_NAME = 'f2ce-backup-target';
const STORE = 'handles';
const KEY = 'vault';

/** Rewrites are debounced: a play session saves in bursts, and each rewrite
 *  re-reads and re-zips every save. */
const DEBOUNCE_MS = 30_000;

export function backupFolderSupported(): boolean {
  return typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';
}

function openHandleDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function storeHandle(handle: FsDirectoryHandle | null): Promise<void> {
  const db = await openHandleDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE, 'readwrite');
      const store = transaction.objectStore(STORE);
      if (handle) store.put(handle, KEY);
      else store.delete(KEY);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } finally {
    db.close();
  }
}

async function loadHandle(): Promise<FsDirectoryHandle | null> {
  if (!backupFolderSupported()) return null;
  let db: IDBDatabase;
  try {
    db = await openHandleDb();
  } catch {
    return null;
  }
  try {
    return await new Promise<FsDirectoryHandle | null>((resolve) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY);
      request.onsuccess = () => resolve((request.result as FsDirectoryHandle | undefined) ?? null);
      request.onerror = () => resolve(null);
    });
  } finally {
    db.close();
  }
}

let connected: FsDirectoryHandle | null = null;
let onDowngrade: (() => void) | null = null;

export const connectedFolderName = (): string | null => connected?.name ?? null;

/**
 * @param interactive  whether a permission prompt is allowed. False at boot,
 *   where there is no user gesture to spend and a prompt would be a popup out
 *   of nowhere.
 */
async function permitted(handle: FsDirectoryHandle, interactive: boolean): Promise<boolean> {
  try {
    const state = (await handle.queryPermission?.({ mode: 'readwrite' })) ?? 'granted';
    if (state === 'granted') return true;
    if (!interactive || state === 'denied') return false;
    return ((await handle.requestPermission?.({ mode: 'readwrite' })) ?? 'denied') === 'granted';
  } catch {
    return false;
  }
}

/** Restores the connection at boot without prompting. */
export async function resumeBackupFolder(): Promise<string | null> {
  const handle = await loadHandle();
  if (!handle) return null;
  if (!(await permitted(handle, false))) {
    // The handle is still ours; the grant simply needs re-confirming with a
    // gesture we do not have. Keep it, run in nudge mode, and let the folder
    // reconnect the next time the player presses the button.
    return null;
  }
  connected = handle;
  setBackupDestination(handle.name);
  return handle.name;
}

export async function connectBackupFolder(): Promise<string> {
  const picker = (window as unknown as {
    showDirectoryPicker: (options?: object) => Promise<FsDirectoryHandle>;
  }).showDirectoryPicker;
  const handle = await picker({ id: 'rerun-vault-backup', mode: 'readwrite', startIn: 'documents' });
  if (!(await permitted(handle, true))) throw new Error('That folder was not granted write access.');
  connected = handle;
  await storeHandle(handle);
  setBackupDestination(handle.name);
  await writeVaultToFolder();
  return handle.name;
}

export async function disconnectBackupFolder(): Promise<void> {
  connected = null;
  setBackupDestination(null);
  await storeHandle(null).catch(() => {});
}

/** Writes (or overwrites) the vault file in the connected folder. */
export async function writeVaultToFolder(): Promise<boolean> {
  if (!connected) return false;
  if (!(await permitted(connected, false))) {
    connected = null;
    setBackupDestination(null);
    onDowngrade?.();
    return false;
  }
  const backup = await buildVaultBackup();
  const file = await connected.getFileHandle(backup.filename, { create: true });
  const writable = await file.createWritable();
  await writable.write(backup.blob);
  await writable.close();
  await markBackedUp();
  return true;
}

/**
 * Rewrites the vault after each new save, coalesced.
 *
 * Driven by the shell's existing new-save signal rather than by a watcher of
 * its own: `rerun:saves-changed` already fires for engine syncs, shell-side
 * mutations and the polling fallback alike.
 */
export function startAutoBackup(opts: { onWritten: () => void; onDowngrade: () => void }): void {
  onDowngrade = opts.onDowngrade;
  let timer: number | undefined;
  window.addEventListener('rerun:saves-changed', () => {
    if (!connected) return;
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = undefined;
      void writeVaultToFolder()
        .then((written) => {
          if (written) opts.onWritten();
        })
        .catch((error) => {
          console.warn('[backup] automatic rewrite failed', error);
          opts.onDowngrade();
        });
    }, DEBOUNCE_MS);
  });
}
